import React, { useState, useEffect } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Badge from '@material-ui/core/Badge';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Divider from '@material-ui/core/Divider';
import Avatar from '@material-ui/core/Avatar';
import { Search, Bell, Moon, Sun, LogOut, User, Settings } from 'lucide-react';
import {
  useApi,
  appThemeApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { parseEntityRef } from '@backstage/catalog-model';
import { useSearchModal } from '@backstage/plugin-search';
import { useUserProfile } from '@backstage/plugin-user-settings';
import {
  useNotificationsApi,
  GetNotificationsResponse,
} from '@backstage/plugin-notifications';
import { Notification } from '@backstage/plugin-notifications-common';
import { Link } from '@backstage/core-components';
import { useNavigate } from 'react-router-dom';
import LogoFull from './LogoFull';

export const TOPNAV_HEIGHT = 52;

const useStyles = makeStyles(theme => ({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1200,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    height: TOPNAV_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    height: 50,
    '& svg, & img': {
      height: 50,
      width: 'auto',
    },
  },
  centerSection: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    padding: '0 24px',
    maxWidth: 600,
    margin: '0 auto',
  },
  searchContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: 380,
    cursor: 'pointer',
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    width: 16,
    height: 16,
    color: theme.palette.text.secondary,
    transition: 'color 0.15s',
  },
  searchInput: {
    width: '100%',
    height: 34,
    borderRadius: 9,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.type === 'dark' ? '#0d0d10' : '#f9fafb',
    color: theme.palette.text.primary,
    fontSize: 12,
    fontFamily: 'inherit',
    padding: '0 10px 0 36px',
    outline: 'none',
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:hover': {
      borderColor: theme.palette.type === 'dark' ? '#404040' : '#d1d5db',
    },
    '&::placeholder': {
      color: theme.palette.text.secondary,
    },
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  iconButton: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  },
  avatar: {
    width: 28,
    height: 28,
    fontSize: 11,
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary,
  },
  avatarImage: {
    width: 28,
    height: 28,
  },
  menuHeader: {
    padding: '12px 16px',
    outline: 'none',
  },
  menuHeaderTitle: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  menuHeaderSubtitle: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  notificationItem: {
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    maxWidth: 320,
  },
  notificationTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  notificationBody: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  notificationTime: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  emptyNotifications: {
    padding: '24px 16px',
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  viewAllLink: {
    padding: '12px 16px',
    display: 'block',
    textAlign: 'center',
    fontSize: 13,
    color: '#F0A31C',
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  signOutItem: {
    color: '#dc2626',
  },
  menuItem: {
    fontSize: 14,
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  menuIcon: {
    fontSize: 18,
    color: theme.palette.text.secondary,
  },
}));

// Helper to get initials from display name
const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

// Helper to format time ago
const formatTimeAgo = (date: Date | string): string => {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
};

export const TopNavigation = () => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const appThemeApi = useApi(appThemeApiRef);
  const identityApi = useApi(identityApiRef);
  const { setOpen: setSearchOpen } = useSearchModal();
  const { displayName, profile } = useUserProfile();

  // Fetch unread notifications using the hook
  const { value: notificationsData } =
    useNotificationsApi<GetNotificationsResponse>(
      api =>
        api.getNotifications({
          read: false,
          limit: 5,
        }),
      [],
    );

  const [notificationsAnchor, setNotificationsAnchor] =
    useState<null | HTMLElement>(null);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [userEntityPath, setUserEntityPath] = useState<string | null>(null);

  // Fetch user entity ref for profile link
  useEffect(() => {
    const fetchUserEntityRef = async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        if (identity?.userEntityRef) {
          // Parse the entity ref to get kind, namespace, and name
          // e.g., "user:default/john.doe" -> { kind: "user", namespace: "default", name: "john.doe" }
          const parsed = parseEntityRef(identity.userEntityRef);
          const namespace = parsed.namespace || 'default';
          setUserEntityPath(
            `/catalog/${namespace}/${parsed.kind}/${parsed.name}`,
          );
        }
      } catch (error) {
        // Fallback to settings if we can't get user entity
        console.warn('Could not fetch user entity ref:', error);
      }
    };
    fetchUserEntityRef();
  }, [identityApi]);

  const isDark = theme.palette.type === 'dark';

  const notifications: Notification[] = notificationsData?.notifications ?? [];
  const unreadCount = notifications.length;

  const handleThemeToggle = () => {
    const themes = appThemeApi.getInstalledThemes();
    const lightTheme = themes.find(
      t => t.id.includes('light') || t.id === 'konteksLight',
    );
    const darkTheme = themes.find(
      t => t.id.includes('dark') || t.id === 'konteksDark',
    );

    if (isDark && lightTheme) {
      appThemeApi.setActiveThemeId(lightTheme.id);
    } else if (!isDark && darkTheme) {
      appThemeApi.setActiveThemeId(darkTheme.id);
    }
  };

  const handleSearchClick = () => {
    setSearchOpen(true);
  };

  const handleSignOut = async () => {
    setProfileAnchor(null);
    await identityApi.signOut();
    // Redirect to home after sign out - the auth system will handle the rest
    window.location.href = '/';
  };

  const handleViewProfile = () => {
    setProfileAnchor(null);
    if (userEntityPath) {
      navigate(userEntityPath);
    } else {
      // Fallback to settings if user entity path is not available
      navigate('/settings');
    }
  };

  const handleSettingsClick = () => {
    setProfileAnchor(null);
    navigate('/settings');
  };

  const handleNotificationClick = (link?: string) => {
    setNotificationsAnchor(null);
    if (link) {
      navigate(link);
    }
  };

  const userEmail = profile.email || '';
  const userInitials = getInitials(displayName);
  const userPicture = profile.picture;

  return (
    <header className={classes.root}>
      {/* Left section - Logo */}
      <div className={classes.leftSection}>
        <Link to="/" aria-label="Home">
          <div className={classes.logo}>
            <LogoFull />
          </div>
        </Link>
      </div>

      {/* Center section - Search */}
      <div className={classes.centerSection}>
        <div className={classes.searchContainer} onClick={handleSearchClick}>
          <Search className={classes.searchIcon} size={16} />
          <input
            type="text"
            placeholder="Search APIs, services, teams..."
            className={classes.searchInput}
            readOnly
          />
        </div>
      </div>

      {/* Right section - Actions */}
      <div className={classes.rightSection}>
        {/* Theme Toggle */}
        <IconButton
          className={classes.iconButton}
          onClick={handleThemeToggle}
          size="small"
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </IconButton>

        {/* Notifications */}
        <IconButton
          className={classes.iconButton}
          onClick={e => setNotificationsAnchor(e.currentTarget)}
          size="small"
          title="Notifications"
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            overlap="rectangular"
            max={99}
          >
            <Bell size={18} />
          </Badge>
        </IconButton>
        <Menu
          anchorEl={notificationsAnchor}
          open={Boolean(notificationsAnchor)}
          onClose={() => setNotificationsAnchor(null)}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <div className={classes.menuHeader}>
            <div className={classes.menuHeaderTitle}>Notifications</div>
            <div className={classes.menuHeaderSubtitle}>
              {unreadCount > 0
                ? `You have ${unreadCount} unread notification${
                    unreadCount > 1 ? 's' : ''
                  }`
                : 'No new notifications'}
            </div>
          </div>
          <Divider />
          {notifications.length > 0 ? (
            <>
              {notifications.map(notification => (
                <MenuItem
                  key={notification.id}
                  className={classes.notificationItem}
                  onClick={() =>
                    handleNotificationClick(notification.payload.link)
                  }
                >
                  <span className={classes.notificationTitle}>
                    {notification.payload.title}
                  </span>
                  {notification.payload.description && (
                    <span className={classes.notificationBody}>
                      {notification.payload.description}
                    </span>
                  )}
                  <span className={classes.notificationTime}>
                    {formatTimeAgo(notification.created)}
                  </span>
                </MenuItem>
              ))}
              <Divider />
              <Link
                to="/notifications"
                className={classes.viewAllLink}
                onClick={() => setNotificationsAnchor(null)}
              >
                View all notifications
              </Link>
            </>
          ) : (
            <div className={classes.emptyNotifications}>
              No new notifications
            </div>
          )}
        </Menu>

        {/* Profile */}
        <IconButton
          className={classes.iconButton}
          onClick={e => setProfileAnchor(e.currentTarget)}
          size="small"
          title={displayName}
        >
          {userPicture ? (
            <Avatar src={userPicture} className={classes.avatarImage} />
          ) : (
            <Avatar className={classes.avatar}>{userInitials}</Avatar>
          )}
        </IconButton>
        <Menu
          anchorEl={profileAnchor}
          open={Boolean(profileAnchor)}
          onClose={() => setProfileAnchor(null)}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <div className={classes.menuHeader}>
            <div className={classes.menuHeaderTitle}>{displayName}</div>
            <div className={classes.menuHeaderSubtitle}>{userEmail}</div>
          </div>
          <Divider />
          <MenuItem onClick={handleViewProfile} className={classes.menuItem}>
            <User size={18} className={classes.menuIcon} />
            Profile
          </MenuItem>
          <MenuItem onClick={handleSettingsClick} className={classes.menuItem}>
            <Settings size={18} className={classes.menuIcon} />
            Settings
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={handleSignOut}
            className={`${classes.menuItem} ${classes.signOutItem}`}
          >
            <LogOut size={18} className={classes.menuIcon} />
            Sign Out
          </MenuItem>
        </Menu>
      </div>
    </header>
  );
};
